import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlContainer, FormGroupDirective, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CodeConversionMstDto, Sgke903aResponseCompanyAreaInfo, Sgke903aResponseInitialhyojiInfo } from 'sg-api';
import { CodeConversionMstService } from '../../../../../../sg-base/sg-base-shared/services/codeConversionMst.service';

@Component({
  selector: 'sgke300d-search-condition',
  templateUrl: './sgke300d-search-condition.component.html',
  styleUrls: ['./sgke300d-search-condition.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class Sgke300dSearchConditionComponent implements OnInit, OnDestroy {
  /** 会社地区情報 */
  @Input() cmpnyAreaList: Array<Sgke903aResponseCompanyAreaInfo>;
  /** 初期表示用会社地区情報（将来用） */
  @Input() cmpnyAreaInit: Sgke903aResponseInitialhyojiInfo;
  /** 検索済フラグ */
  @Input() searched: boolean;
  /** 業務日付 */
  @Input() businessDate: Date = null;

  /** クリアボタン押下イベント */
  @Output() onClickClearEvent = new EventEmitter();

  /** 購読を停止するためのSubject */
  private onDestroy$ = new Subject();

  /** 検索フォーム */
  searchConditionForm: UntypedFormGroup;

  /** 会社リスト */
  companyList: Array<Sgke903aResponseCompanyAreaInfo>;
  /** 地区リスト */
  chikuList: Array<Sgke903aResponseCompanyAreaInfo>;
  /** コード変換マスターマップ */
  codeMap: Map<string, CodeConversionMstDto[]>;

  /** 督促管理区分コード 画面項目IDコード値 */
  static S300090: string = "S300090";
  /** 料金区分コード 画面項目IDコード値 */
  static S300620: string = "S300620";
  /** 督促ステータスード 画面項目IDコード値 */
  static S301520: string = "S301520";

  // 督促管理区分コード コード値
  /** 無訪問 */
  static S300090_NOVISIT: String = "1";
  /** センター管理 */
  static S300090_CENTERMANAGE: String = "2";
  /** 営業統括管理 */
  static S300090_SALESMANAGE: String = "3";

  /**
   * コンストラクタ
   * @param parent 親FormGroup
   * @param fb FormBuilder
   * @param codeConvert コード変換マスター取得サービス
   */
  constructor(
    private parent: FormGroupDirective,
    private fb: UntypedFormBuilder,
    private codeConvert: CodeConversionMstService,
    private cb: ChangeDetectorRef,
  ) {
  }
  /**
   * コンポーネント初期化処理
   */
  ngOnInit(): void {
    this.searchConditionForm = this.fb.group({
      pressManagementdivisionCode: this.fb.nonNullable.control({
        value: [Sgke300dSearchConditionComponent.S300090_NOVISIT,
        Sgke300dSearchConditionComponent.S300090_CENTERMANAGE], disabled: false
      }, Validators.required),
      companycode: this.fb.nonNullable.control({ value: null, disabled: false }),
      areacode: this.fb.nonNullable.control({ value: null, disabled: false }),
      chargeDivcd: [{ value: null, disabled: false }],
      searchconditionPressStatuscode: [{ value: null, disabled: false }],
      koufuYearmonthdateStart: [{ value: null, disabled: false }],
      koufuYearmonthdateEnd: this.fb.nonNullable.control({ value: this.businessDate, disabled: false }),
    });


    // 親コンポーネントのformに検索条件formを追加
    this.parent.form.addControl("cndt", this.searchConditionForm);

    // コード変換マスタ取得
    let dispItemIdList = [
      Sgke300dSearchConditionComponent.S300090,
      Sgke300dSearchConditionComponent.S300620,
      Sgke300dSearchConditionComponent.S301520,
    ];
    // コード変換マスタ共通コンポーネント呼び出し
    this.codeConvert.getCodeConversionMst(dispItemIdList).pipe(takeUntil(this.onDestroy$)).subscribe(codeMap => {
      // ローカルに保存
      this.codeMap = codeMap;
    });
    this.cb.detectChanges();

    this.onChangeTokusokuKubun();
  }
  /**
   * コンポーネント終了処理
   */
  ngOnDestroy(): void {
    this.searchConditionForm.clearValidators();
    this.searchConditionForm.updateValueAndValidity();
    this.searchConditionForm.removeControl('cndt');
    this.onDestroy$.next();
  }

  /** コントロール取得 */
  getControl(ctrlName): UntypedFormControl {
    return this.searchConditionForm.get(ctrlName) as UntypedFormControl;
  }

  /**
   * 督促管理区分変更時処理
   */
  onChangeTokusokuKubun() {
    ['companycode', 'areacode'].forEach(ctrlName => {
      let ctrl = this.getControl(ctrlName);
      let tokusoku = this.getControl('pressManagementdivisionCode');

      if (tokusoku.value.includes(Sgke300dSearchConditionComponent.S300090_SALESMANAGE)) {
        // 督促管理区分＝営業統括管理の場合
        ctrl.enable();
        if (ctrlName == 'companycode') {
          ctrl?.setValue(null);
          // this.changeChikuList();
          this.companyList = this.cmpnyAreaList.reduce((acc, cur) => {
            // 会社コードが重複していない要素のみpushする。
            if (!acc.some(company => company.companycode === cur.companycode)) {
              acc.push(cur);
            }
            return acc;
          }, new Array<Sgke903aResponseCompanyAreaInfo>);
        } else if (ctrlName == 'areacode') {
          ctrl?.setValue(null);
          this.chikuList = null;
        }
      } else {
        // それ以外
        ctrl.disable();
        ctrl.reset();
      }
    });
  }

  /**
   * 会社ドロップダウン変化時処理
   * @param companycode 会社コード
   */
  onChangeCompany(companycode: string) {
    // 会社コードリストの値が変わった場合
    const chikuCtl = this.getControl('areacode') as UntypedFormControl;
    chikuCtl?.setValue(null);
    this.chikuList = this.cmpnyAreaList.filter(list => list.companycode == companycode);
  }

  /**
   * クリアボタン押下時処理
   */
  onClickClear(): void {
    this.searchConditionForm.reset();
    this.onClickClearEvent.emit();
    this.onChangeTokusokuKubun();
  }
}
